/**
 * Response Builder Router
 * 
 * Routes built exclusively from packages/routing constants.
 * All routes use placeholder components per PHASE_B_WORK_ORDERS.md B3.
 */

import { createBrowserRouter } from 'react-router-dom';
import { ROUTES } from '@threadlock/routing';
import { RequireAuth, RequireWorkspaceAccess, RequireRole } from '@threadlock/guards';

// Import page components
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';

import DashboardPage from './pages/app/DashboardPage';
import LibraryPage from './pages/app/LibraryPage';
import ProjectsPage from './pages/app/ProjectsPage';
import ProjectNewPage from './pages/app/ProjectNewPage';
import ProjectDetailPage from './pages/app/ProjectDetailPage';
import ProjectQuestionsPage from './pages/app/ProjectQuestionsPage';
import QuestionDetailPage from './pages/app/QuestionDetailPage';
import ExportsPage from './pages/app/ExportsPage';
import AdminPage from './pages/app/AdminPage';
import SettingsPage from './pages/app/SettingsPage';
import BillingPage from './pages/app/BillingPage';
import StatusPage from './pages/app/StatusPage';

// Phase C: Library pages
import LibraryDetailPage from './pages/app/LibraryDetailPage';
import LibraryUploadPage from './pages/app/LibraryUploadPage';

// Phase CΔ-2: Dev-only workspace isolation test (MUST BE EXCLUDED FROM PRODUCTION)
import DevWorkspaceIsolationTest from './pages/app/DevWorkspaceIsolationTest';

import ForbiddenPage from './pages/system/ForbiddenPage';
import NotFoundPage from './pages/system/NotFoundPage';

import AppShell from './app-shell/AppShell';
import { useAuthState } from './config/auth';
import { useWorkspaceState } from './config/workspace';

// Guard wrappers that connect config state
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { authenticated, authLoading, emailVerified } = useAuthState();
  return (
    <RequireAuth
      authenticated={authenticated}
      emailVerified={emailVerified}
      loading={authLoading}
      redirectTo={ROUTES.LOGIN}
      verificationRedirect={ROUTES.HOME}
    >
      {children}
    </RequireAuth>
  );
}

function WorkspaceGuard({ children }: { children: React.ReactNode }) {
  const { hasAccess, workspaceLoading } = useWorkspaceState();
  return (
    <RequireWorkspaceAccess
      hasAccess={hasAccess}
      loading={workspaceLoading}
      redirectTo={ROUTES.FORBIDDEN}
    >
      {children}
    </RequireWorkspaceAccess>
  );
}

export const router = createBrowserRouter([
  // Public routes
  {
    path: ROUTES.HOME,
    element: <VerifyEmailPage />, // Δ-B2: Redirect point for unverified users
  },
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
  },
  {
    path: ROUTES.SIGNUP,
    element: <SignupPage />,
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    element: <ForgotPasswordPage />,
  },

  // Authenticated routes - all wrapped in AppShell
  {
    path: '/app',
    element: (
      <AuthGuard>
        <WorkspaceGuard>
          <AppShell />
        </WorkspaceGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: ROUTES.DASHBOARD,
        element: <DashboardPage />,
      },
      {
        path: ROUTES.LIBRARY,
        element: <LibraryPage />,
      },
      {
        path: ROUTES.LIBRARY_UPLOAD,
        element: <LibraryUploadPage />,
      },
      {
        path: ROUTES.LIBRARY_DOCUMENT,
        element: <LibraryDetailPage />,
      },
      {
        path: ROUTES.PROJECTS,
        element: <ProjectsPage />,
      },
      {
        path: ROUTES.PROJECTS_NEW,
        element: <ProjectNewPage />,
      },
      {
        path: ROUTES.PROJECT_DETAIL,
        element: <ProjectDetailPage />,
      },
      {
        path: ROUTES.PROJECT_QUESTIONS,
        element: <ProjectQuestionsPage />,
      },
      {
        path: ROUTES.PROJECT_QUESTION_DETAIL,
        element: <QuestionDetailPage />,
      },
      {
        path: ROUTES.EXPORTS,
        element: <ExportsPage />,
      },
      {
        path: ROUTES.ADMIN,
        element: (
          <RequireRole minRole="admin" userRole="viewer" loading={false} redirectTo={ROUTES.FORBIDDEN}>
            <AdminPage />
          </RequireRole>
        ),
      },
      {
        path: ROUTES.SETTINGS,
        element: <SettingsPage />,
      },
      {
        path: ROUTES.BILLING,
        element: (
          <RequireRole minRole="owner" userRole="viewer" loading={false} redirectTo={ROUTES.FORBIDDEN}>
            <BillingPage />
          </RequireRole>
        ),
      },
      {
        path: ROUTES.STATUS,
        element: <StatusPage />,
      },
      // Phase CΔ-2: Dev-only workspace isolation test
      // WARNING: This route MUST be removed or disabled in production builds
      {
        path: ROUTES.DEV_WORKSPACE_ISOLATION_TEST,
        element: process.env.NODE_ENV === 'development' ? <DevWorkspaceIsolationTest /> : <NotFoundPage />,
      },
      {
        path: ROUTES.FORBIDDEN,
        element: <ForbiddenPage />,
      },
      {
        path: ROUTES.NOT_FOUND,
        element: <NotFoundPage />,
      },
    ],
  },

  // Catch-all
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
